/**
 * IssueGiftCardModal.tsx
 *
 * Issue one or more gift cards and collect payment from the purchaser in one flow.
 * Supports the same payment flow as ChargePaymentModal: merchant selection,
 * saved card / terminal / new card via Forward, plus a Cash option.
 * Multiple recipients per transaction (self or other patients).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DollarSign, CreditCard, Gift, Plus, Trash2, Loader2 } from 'lucide-react';

import {
  chargeForwardPayment,
  createPatientPayment,
  listBillingMethods,
  listQuoteItems,
  markTerminalTransactionComplete,
  type BillingMethod,
  type Merchant,
  type PaymentTerminal,
  type TerminalCheckoutResult,
  type TerminalTokenizeResult,
} from '@/services/payments.service';
import { issueGiftCard } from '@/services/gift-cards.service';
import { MerchantSelector } from '@/components/payments/MerchantSelector';
import { PaymentSourceSelector, parseTerminalToken } from '@/components/payments/PaymentSourceSelector';
import { ForwardBillingMethodCapture } from '@/components/payments/ForwardBillingMethodCapture';
import { TerminalCheckoutPanel, type TerminalFlowResult } from '@/components/payments/TerminalCheckoutPanel';
import { EnrollmentLinkageFields } from '@/components/memberships/EnrollmentLinkageFields';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import { useAuth } from '@/hooks/useAuth';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { todayInTz } from '@/lib/facilityTimezone';
import { MAX_GIFT_CARD_AMOUNT } from '@/lib/giftCards';
import type { PatientSummary } from '@/types/patient.types';
import { useIsBelow } from '@/hooks/useBreakpoint';
import { useVisualViewportVars } from '@/hooks/useVisualViewportVars';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface IssueGiftCardModalProps {
  open: boolean;
  onClose: () => void;
  onIssued: () => void;
  defaultPurchaser?: PatientSummary | null;
  /** When true, the purchaser is fixed to defaultPurchaser and shown read-only. */
  lockPurchaser?: boolean;
}

type PayMode = 'card' | 'cash' | 'comp';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GiftCardEntry {
  key: string;
  productId: number | null;
  recipientIsSelf: boolean;
  recipient: PatientSummary | null;
  amount: string;
  title: string;
  serialNumber: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtCurrency(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

/**
 * Build a plain-language note recording which card went to which patient
 * (name + id), so the payment is self-documenting — especially when the
 * purchaser and the recipient are different people.
 */
function buildGiftCardNote(entries: GiftCardEntry[], purchaser: PatientSummary): string {
  const lines = entries.map((e) => {
    const recipient = e.recipientIsSelf ? purchaser : e.recipient;
    const amt = fmtCurrency(parseFloat(e.amount) || 0);
    const who = recipient ? `${recipient.displayName} (#${recipient.patientId})` : 'unknown recipient';
    return `${amt} to ${who}`;
  });
  return entries.length === 1
    ? `Gift card issued: ${lines[0]}`
    : `Gift cards issued:\n${lines.map((l) => `• ${l}`).join('\n')}`;
}

let _keyCounter = 0;
function newEntry(isSelf = true, defaultProductId: number | null = null): GiftCardEntry {
  return {
    key: String(++_keyCounter),
    productId: defaultProductId,
    recipientIsSelf: isSelf,
    recipient: null,
    amount: '',
    title: '',
    serialNumber: '',
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function IssueGiftCardModal({ open, onClose, onIssued, defaultPurchaser, lockPurchaser }: IssueGiftCardModalProps) {
  // Comp (no-payment) issuance is gated to the same rule as the gift-card
  // management permission (matches the backend POST /gift-cards gate).
  const { user, hasPermission } = useAuth();
  const userTz = useUserTimezone();
  const canComp = (user?.userLevel ?? 0) > 3 || hasPermission(14);
  // ── Gift credit products ────────────────────────────────────────────────
  const { data: allQuoteItems = [] } = useQuery({
    queryKey: ['payment-quote-items-all'],
    queryFn: () => listQuoteItems({}),
    staleTime: 5 * 60_000,
  });

  const giftCreditProducts = useMemo(
    () => allQuoteItems.filter((item) => item.productType === 1),
    [allQuoteItems],
  );

  const defaultProductId = giftCreditProducts.length === 1 ? giftCreditProducts[0].itemId : null;

  // ── Purchaser (who pays) ─────────────────────────────────────────────────
  const [purchaser, setPurchaser] = useState<PatientSummary | null>(null);

  // ── Payment: merchant + source (mirrors ChargePaymentModal) ─────────────
  const [payMode, setPayMode] = useState<PayMode>('card');
  const [merchantId, setMerchantId] = useState<number | null>(null);
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [paymentToken, setPaymentToken] = useState('new');
  const [selectedMethod, setSelectedMethod] = useState<BillingMethod | null>(null);
  const [selectedTerminal, setSelectedTerminal] = useState<PaymentTerminal | null>(null);
  const [terminalResult, setTerminalResult] = useState<TerminalFlowResult | null>(null);
  const [facilityId, setFacilityId] = useState('');
  const [cardPreselected, setCardPreselected] = useState(false);

  const [memo, setMemo] = useState('');

  // ── Gift card entries ────────────────────────────────────────────────────
  const [entries, setEntries] = useState<GiftCardEntry[]>([newEntry(true, null)]);

  // ── Submission ───────────────────────────────────────────────────────────
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // ── Reset on open ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setPayMode('card');
    setMerchantId(null);
    setMerchant(null);
    setPaymentToken('new');
    setSelectedMethod(null);
    setSelectedTerminal(null);
    setTerminalResult(null);
    setFacilityId('');
    setCardPreselected(false);
    setMemo('');
    setEntries([newEntry(true, defaultProductId)]);
    setSubmitError(null);
    setSuccess(false);
    if (defaultPurchaser) {
      setPurchaser(defaultPurchaser);
    } else {
      setPurchaser(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-set productId on entries when products load (single product case)
  useEffect(() => {
    if (defaultProductId === null) return;
    setEntries((prev) =>
      prev.map((e) => (e.productId === null ? { ...e, productId: defaultProductId } : e)),
    );
  }, [defaultProductId]);

  // ── Purchaser saved cards (for default preselect in Card mode) ───────────
  const { data: billingMethods } = useQuery({
    queryKey: ['patient-billing-methods', purchaser?.patientId],
    queryFn: () => listBillingMethods(purchaser!.patientId),
    enabled: !!purchaser,
    staleTime: 60_000,
  });

  // When Card mode is active, preselect the purchaser's default saved card
  // (falling back to their first saved card). Runs once per open; the user is
  // free to switch to "New card" or another source afterwards.
  useEffect(() => {
    if (payMode !== 'card' || cardPreselected || !purchaser) return;
    const activeCards = (billingMethods ?? []).filter((m) => m.endDate == null);
    if (activeCards.length === 0) return;
    const preferred = activeCards.find((m) => m.isDefault) ?? activeCards[0];
    setPaymentToken(preferred.token);
    setSelectedMethod(preferred);
    setCardPreselected(true);
  }, [payMode, cardPreselected, purchaser, billingMethods]);

  // ── Handlers (mirror ChargePaymentModal) ───────────────────────────────────
  const handleMerchantChange = useCallback((id: number, m: Merchant) => {
    setMerchantId(id);
    setMerchant(m);
    setTerminalResult(null);
  }, []);

  const handleSourceChange = useCallback(
    (token: string, method: BillingMethod | null, terminal: PaymentTerminal | null = null) => {
      setPaymentToken(token);
      setSelectedMethod(method);
      setSelectedTerminal(terminal);
      setTerminalResult(null);
    }, []);

  const handleNewSourceSaved = useCallback((method: BillingMethod) => {
    setPaymentToken(method.token);
    setSelectedMethod(method);
    setTerminalResult(null);
  }, []);

  const handleTerminalResult = useCallback((result: TerminalFlowResult) => {
    setTerminalResult(result);
  }, []);

  // ── Entry helpers ─────────────────────────────────────────────────────────
  function updateEntry(key: string, patch: Partial<GiftCardEntry>) {
    setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ...patch } : e)));
  }

  function removeEntry(key: string) {
    setEntries((prev) => prev.filter((e) => e.key !== key));
  }

  // ── Derived ───────────────────────────────────────────────────────────────
  const total = entries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const terminalTokenId = parseTerminalToken(paymentToken);
  const isTerminalMode = terminalTokenId !== null;

  // ── Submit ────────────────────────────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: async () => {
      if (!purchaser) throw new Error('Select a purchaser');
      if (total <= 0) throw new Error('At least one gift card must have a valid amount');

      for (const e of entries) {
        const amt = parseFloat(e.amount);
        if (!amt || amt <= 0) throw new Error('All gift card amounts must be greater than $0');
        if (amt > MAX_GIFT_CARD_AMOUNT) throw new Error(`Gift card amount cannot exceed ${fmtCurrency(MAX_GIFT_CARD_AMOUNT)}`);
        if (payMode !== 'comp' && !e.productId) throw new Error('Select a gift card product for each card');
        if (!e.recipientIsSelf && !e.recipient) throw new Error('Select a recipient for each gift card');
      }

      // ── Comp path (no payment collected) ────────────────────────────────
      if (payMode === 'comp') {
        for (const e of entries) {
          const amt = parseFloat(e.amount);
          await issueGiftCard({
            originalValue: amt,
            issuedToPatientId: e.recipientIsSelf ? purchaser.patientId : e.recipient?.patientId,
            serialNumber: e.serialNumber.trim() || undefined,
            title: e.title.trim() || `$${amt.toFixed(2)} Gift Card`,
            notes: memo.trim() || undefined,
          });
        }
        return;
      }

      const items = entries.map((e) => ({
        itemId: e.productId!,
        price: parseFloat(e.amount),
        quantity: 1,
        taxable: false,
        salesTax: 0,
        productType: 1,
        creditValue: parseFloat(e.amount),
        serialNumber: e.serialNumber.trim() || undefined,
        giftCardTitle: e.title.trim() || undefined,
        targetGiftCardPatientId: e.recipientIsSelf
          ? purchaser.patientId
          : e.recipient?.patientId,
      }));

      // Append an automatic note recording which card went to which patient,
      // preserving any memo the user typed.
      const giftCardNote = buildGiftCardNote(entries, purchaser);
      const mergedMemo = [memo.trim(), giftCardNote].filter(Boolean).join('\n\n') || undefined;

      // ── Cash path ──────────────────────────────────────────────────────
      if (payMode === 'cash') {
        if (!facilityId) throw new Error('Select a facility.');
        await createPatientPayment(purchaser.patientId, {
          amount: total,
          // Today in the user's zone — a UTC day misdates evening sales.
          date: todayInTz(userTz),
          method: 'cash',
          strPayType: 'Gift Card Purchase',
          memo: mergedMemo,
          accountAuthnetID: merchantId ?? undefined,
          facilityID: Number(facilityId) || undefined,
          items,
        });
      } else {
        // ── Card / terminal path (mirrors ChargePaymentModal) ────────────
        if (!merchantId) throw new Error('Select a merchant account');

        let terminalMethodTitle: string | null = null;
        let terminalTxId: string | null = null;
        let terminalPiId: string | null = null;
        let terminalStatus: string | null = null;
        let terminalRef: string | null = null;
        let paymentMethodToken: string | null = selectedMethod?.token ?? null;
        let isTerminalCheckout = false;

        if (isTerminalMode && terminalResult) {
          if (terminalResult.mode === 'checkout') {
            const data: TerminalCheckoutResult = terminalResult.data;
            terminalTxId = data.transaction_id ?? data.payment_intent_id;
            terminalPiId = data.payment_intent_id ?? null;
            terminalStatus = data.status;
            terminalRef = data.transaction_id ?? data.payment_intent_id;
            paymentMethodToken = data.token ?? paymentMethodToken;
            terminalMethodTitle = data.card_brand
              ? `Terminal ${data.card_brand}${data.masked_pan ? ' ****' + data.masked_pan : ''}`
              : 'Terminal';
            isTerminalCheckout = true;
          } else {
            const data: TerminalTokenizeResult = terminalResult.data;
            paymentMethodToken = data.token ?? paymentMethodToken;
            terminalMethodTitle = data.card_brand
              ? `Terminal ${data.card_brand}${data.masked_pan ? ' ****' + data.masked_pan : ''}`
              : 'Terminal';
          }
        } else if (!isTerminalMode && !selectedMethod) {
          throw new Error('Select a payment source or complete the new card entry first.');
        }

        const willRunGatewayCharge = !isTerminalCheckout && total > 0;

        const payment = await createPatientPayment(purchaser.patientId, {
          amount: total,
          date: todayInTz(userTz),
          method: terminalMethodTitle ?? selectedMethod?.title ?? 'Forward',
          strPayType: 'Gift Card Purchase',
          memo: mergedMemo,
          referenceNumber: terminalRef ?? undefined,
          paymentMethodID: selectedMethod?.paymentMethodID ?? undefined,
          paymentMethodToken: paymentMethodToken ?? undefined,
          accountAuthnetID: merchantId,
          facilityID: merchant?.facilityId ?? undefined,
          chargeAmount: isTerminalCheckout ? total : undefined,
          gatewayTransactionId: isTerminalCheckout ? (terminalTxId ?? undefined) : undefined,
          gatewayStatus: isTerminalCheckout ? (terminalStatus ?? undefined) : undefined,
          paymentIntentId: isTerminalCheckout ? (terminalPiId ?? undefined) : undefined,
          deferPaymentTrigger: willRunGatewayCharge || undefined,
          items,
        });

        if (isTerminalCheckout && terminalResult?.mode === 'checkout') {
          const logId = terminalResult.data.transaction_log_id;
          if (typeof logId === 'number' && logId > 0) {
            void markTerminalTransactionComplete(logId, payment.paymentID).catch(() => {});
          }
        }

        if (willRunGatewayCharge && paymentMethodToken && merchantId) {
          const charge = await chargeForwardPayment(payment.paymentID, {
            merchant_id: merchantId,
            payment_method_id: paymentMethodToken,
            amount: total,
            patient_id: purchaser.patientId,
          });

          if (!charge.success) {
            throw new Error(charge.message || 'Transaction error: Payment was declined.');
          }
        }
      }
    },
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (err) => {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    },
  });

  function handleClose() {
    onClose();
  }

  function handleDone() {
    onIssued();
  }

  /*
    SCRUM-1413. This modal is hand-rolled rather than the shared `Modal`, so it had none
    of that component's phone handling — and its symptoms were exactly the ones that
    absence produces.

    `max-h-[90vh]` is a LAYOUT-viewport unit. The on-screen keyboard does not shrink the
    layout viewport; it shrinks the VISUAL one and slides it down inside it. So the panel
    kept its full pre-keyboard height while the visible band moved, putting the
    `flex-shrink-0` header above it and the footer below it — "the modal header
    disappears, so there is no way to close the modal while typing" and "the footer also
    disappears behind the keyboard". With no scroll lock either, the document drifted
    underneath and the shifted position stuck after the keyboard closed.

    Adopting the two primitives the shared Modal uses, rather than converting the whole
    component: the shell here is already header / `flex-1 overflow-y-auto` body / footer
    inside a `fixed inset-0` centring wrapper — structurally the same thing — so the
    primitives drop in, while a conversion would mean rewriting the header and footer
    into Modal's props for no behavioural gain.

    Both hooks are above the early return below, called unconditionally.
  */
  const isSheet = useIsBelow('md');
  useVisualViewportVars(open && isSheet);
  useBodyScrollLock(open);

  if (!open) return null;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 max-md:top-visual-viewport max-md:h-visual-viewport max-md:bottom-auto max-md:items-stretch max-md:p-0 max-md:pt-sheet-inset">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col max-md:h-full max-md:max-h-full max-md:rounded-b-none">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">Issue Gift Card(s)</h2>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none font-light"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {success ? (
            /* ── Success state ── */
            <div className="space-y-4">
              <div className="mx-auto w-12 h-12 flex items-center justify-center rounded-full bg-green-100">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-center text-sm font-medium text-slate-700">
                {entries.length === 1 ? 'Gift card issued!' : `${entries.length} gift cards issued!`}
                {' '}
                {payMode === 'comp'
                  ? `Complimentary — no payment collected for ${purchaser?.displayName}.`
                  : `Payment of ${fmtCurrency(total)} recorded for ${purchaser?.displayName}.`}
              </p>
            </div>
          ) : (
            /* ── Form ── */
            <div className="space-y-6">

              {/* ── Purchaser ──────────────────────────────────────────── */}
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Purchaser</p>
                {lockPurchaser && purchaser ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                    {purchaser.displayName}
                  </div>
                ) : (
                  <PatientSearchInput
                    onSelect={(p) => setPurchaser(p)}
                    selectedPatient={purchaser}
                    placeholder="Search patient paying for card(s)…"
                  />
                )}
              </div>

              {/* ── Payment Method ──────────────────────────────────────── */}
              {purchaser && (
                <div className="space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Payment</p>

                  {/* Card / Cash / Comp toggle */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPayMode('card')}
                      className={`flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                        payMode === 'card'
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <CreditCard className="h-4 w-4" /> Card / Terminal
                    </button>
                    <button
                      type="button"
                      onClick={() => setPayMode('cash')}
                      className={`flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                        payMode === 'cash'
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <DollarSign className="h-4 w-4" /> Cash
                    </button>
                    {canComp && (
                      <button
                        type="button"
                        onClick={() => setPayMode('comp')}
                        className={`flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                          payMode === 'comp'
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <Gift className="h-4 w-4" /> Comp
                      </button>
                    )}
                  </div>

                  {payMode === 'card' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <PaymentSourceSelector
                          patientId={purchaser.patientId}
                          value={paymentToken}
                          merchantId={merchantId}
                          onChange={handleSourceChange}
                        />
                        <MerchantSelector value={merchantId} onChange={handleMerchantChange} />
                      </div>

                      {paymentToken === 'new' && (
                        <ForwardBillingMethodCapture
                          active
                          patientId={purchaser.patientId}
                          merchantId={merchantId}
                          onSaved={handleNewSourceSaved}
                        />
                      )}

                      {isTerminalMode && selectedTerminal && (
                        <TerminalCheckoutPanel
                          terminal={selectedTerminal}
                          patientId={purchaser.patientId}
                          amount={total}
                          merchantId={merchantId}
                          mode="checkout"
                          onSuccess={handleTerminalResult}
                          onReset={() => setTerminalResult(null)}
                        />
                      )}
                    </>
                  )}

                  {payMode === 'cash' && (
                    <EnrollmentLinkageFields
                      patientId={purchaser.patientId}
                      showPlanQuote={false}
                      facilityRequired
                      planId="" onPlanIdChange={() => {}}
                      quoteId="" onQuoteIdChange={() => {}}
                      facilityId={facilityId} onFacilityIdChange={setFacilityId}
                    />
                  )}

                  {payMode === 'comp' && (
                    <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      Complimentary — no payment will be collected.
                    </p>
                  )}
                </div>
              )}

              {/* ── Gift Cards ──────────────────────────────────────────── */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Gift Cards to Issue</p>
                <div className="space-y-3">
                  {entries.map((entry, idx) => (
                    <div key={entry.key} className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                      {/* Card header */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">Card #{idx + 1}</span>
                        {entries.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeEntry(entry.key)}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                            aria-label="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {/* Recipient */}
                      <div>
                        <p className="mb-1.5 text-xs font-medium text-slate-600">Recipient</p>
                        <div className="flex gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() => updateEntry(entry.key, { recipientIsSelf: true, recipient: null })}
                            className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${entry.recipientIsSelf ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                          >
                            {purchaser ? purchaser.displayName : 'Self (Purchaser)'}
                          </button>
                          <button
                            type="button"
                            onClick={() => updateEntry(entry.key, { recipientIsSelf: false })}
                            className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${!entry.recipientIsSelf ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                          >
                            Someone else
                          </button>
                        </div>
                        {!entry.recipientIsSelf && (
                          <PatientSearchInput
                            onSelect={(p) => updateEntry(entry.key, { recipient: p })}
                            selectedPatient={entry.recipient}
                            placeholder="Search recipient…"
                          />
                        )}
                      </div>

                      {/* Amount + Title row */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Amount ($) *</label>
                          <input
                            type="number"
                            min="0.01"
                            max={MAX_GIFT_CARD_AMOUNT}
                            step="0.01"
                            placeholder="0.00"
                            value={entry.amount}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const n = parseFloat(raw);
                              updateEntry(entry.key, {
                                amount: !Number.isNaN(n) && n > MAX_GIFT_CARD_AMOUNT
                                  ? String(MAX_GIFT_CARD_AMOUNT)
                                  : raw,
                              });
                            }}
                            className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Title <span className="text-slate-400">(optional)</span></label>
                          <input
                            type="text"
                            placeholder='e.g. "Birthday Gift"'
                            value={entry.title}
                            onChange={(e) => updateEntry(entry.key, { title: e.target.value })}
                            className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </div>

                      {/* Product + Serial row */}
                      <div className="grid grid-cols-2 gap-3">
                        {payMode !== 'comp' && (
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Gift Card Product *</label>
                          {giftCreditProducts.length <= 1 ? (
                            <div className="border border-slate-200 bg-white rounded-lg px-3 py-2 text-sm text-slate-700">
                              {giftCreditProducts.length === 1
                                ? giftCreditProducts[0].name
                                : <span className="text-slate-400 italic">No gift credit products found</span>}
                            </div>
                          ) : (
                            <select
                              value={entry.productId ?? ''}
                              onChange={(e) => updateEntry(entry.key, { productId: e.target.value ? Number(e.target.value) : null })}
                              className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                              <option value="">Select gift card product…</option>
                              {giftCreditProducts.map((p) => (
                                <option key={p.itemId} value={p.itemId}>{p.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        )}
                        <div className={payMode === 'comp' ? 'col-span-2' : undefined}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Serial <span className="text-slate-400">(auto if blank)</span></label>
                          <input
                            type="text"
                            placeholder="Auto-generated"
                            value={entry.serialNumber}
                            onChange={(e) => updateEntry(entry.key, { serialNumber: e.target.value })}
                            className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => setEntries((prev) => [...prev, newEntry(false, defaultProductId)])}
                  className="mt-2 flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  <Plus className="h-4 w-4" /> Add Another Gift Card
                </button>
              </div>

              {/* ── Memo ────────────────────────────────────────────────── */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Memo <span className="text-slate-400">(optional)</span></label>
                <textarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="Internal note or occasion…"
                  rows={2}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {submitError && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 flex-shrink-0">
          {success ? (
            <>
              <span />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setSuccess(false);
                    setEntries([newEntry(true, defaultProductId)]);
                    setSubmitError(null);
                  }}
                  className="border border-slate-300 hover:bg-slate-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Issue Another
                </button>
                <button
                  onClick={handleDone}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm text-slate-600">
                Total: <span className="font-bold text-slate-900">{fmtCurrency(total)}</span>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  disabled={mutation.isPending}
                  className="border border-slate-300 hover:bg-slate-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!purchaser || total <= 0 || mutation.isPending || (payMode === 'cash' && !facilityId)}
                  onClick={() => { setSubmitError(null); mutation.mutate(); }}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  {mutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Processing…
                    </span>
                  ) : payMode === 'comp' ? (
                    entries.length === 1 ? 'Add Gift Card' : 'Add Gift Cards'
                  ) : (
                    `Issue & Collect ${total > 0 ? fmtCurrency(total) : ''}`
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
